import React, { useState, useEffect, useRef } from 'react';
import Modal from '../../../../../../components/common/Modal';
import Button from '../../../../../../components/common/Button/Button';
import Badge from '../../../../../../components/common/Badge/Badge';
import SearchFilter from '../../../../../../components/data/SearchFilter';
import { translate, verifyHideCountryItems } from '../../../../../../utils/Common';
import { getClientConfRoleInfo, getClientTypeInfo } from '../../../../../../utils/modules/clients';
import { useClient } from '../../../../../../hooks/useClient';
import { usePlatforms } from '../../../../../../hooks/usePlatforms';
import { verifyAccessViewPermissions } from '../../../../../../config/permissions';
import { formatClientName } from '../../utils/companyFormatters';
import { getStatusVariant } from '../../utils/companyHelpers';
import { FaSpinner } from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import { BiPackage } from 'react-icons/bi';
import { FaRegBuilding } from 'react-icons/fa';
import styles from './AddClientModal.module.css';
import ignoreAbort from '../../../../../../utils/ignoreAbort';

const AddClientModal = ({ isOpen, onClose, company, showSuccess, showError, onClientAdded, canUpdate: canUpdateProp = true }) => {
  const { getClientsByFilter } = useClient();
  const { assignClientToCompany, getAssignedMembersById } = usePlatforms();
  const permissionsPath = "/dashboard/companies";
  const canUpdatePermission = verifyAccessViewPermissions(["update"], { path: permissionsPath });
  const canUpdate = Boolean(canUpdateProp) && canUpdatePermission;
  
  const [filters, setFilters] = useState({});
  const [selectedField, setSelectedField] = useState('name');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedClientType, setSelectedClientType] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigningClientId, setAssigningClientId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [assignedClientIds, setAssignedClientIds] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [addedCountInSession, setAddedCountInSession] = useState(0);
  
  const abortControllerRef = useRef(null);
  const isInitialMount = useRef(true);

  // Definir opciones para el selector de campo (igual que en Clients/index.jsx)
  const fieldOptions = [
    {
      value: 'name',
      label: translate('dashboard.clients.filters.namePlaceholder'),
    },
    {
      value: 'lastName',
      label: translate('dashboard.clients.filters.lastNamePlaceholder'),
    },
    {
      value: 'email',
      label: translate('dashboard.clients.filters.emailPlaceholder'),
    },
    {
      value: 'clientId',
      label: translate('dashboard.clients.filters.clientIdPlaceholder'),
    },
    {
      value: 'userName',
      label: translate('dashboard.clients.filters.userNamePlaceholder'),
    },
    {
      value: 'phoneNumber',
      label: translate('dashboard.clients.filters.phoneNumberPlaceholder'),
    },
  ];

  // Agregar externalId si el país no es US
  if (!verifyHideCountryItems(['us'])) {
    fieldOptions.push({
      value: 'externalId',
      label: translate('dashboard.clients.filters.externalIdPlaceholder'),
    });
  }

  // Opciones para el selector de status (igual que en Clients/index.jsx)
  const statusOptions = [
    {
      value: 'all',
      label: translate('dashboard.clients.filters.allStatus') || 'Todos los estados',
      icon: <FaIcons.FaList size={16} />,
      showLabel: true,
    },
    {
      value: '1',
      label: translate('dashboard.clients.status.active'),
      icon: <FaIcons.FaCheckCircle size={16} />,
      showLabel: true,
    },
    {
      value: '2',
      label: translate('dashboard.clients.status.inactive'),
      icon: <FaIcons.FaTimesCircle size={16} />,
      showLabel: true,
    },
    {
      value: '3',
      label: translate('dashboard.clients.status.pending'),
      icon: <FaIcons.FaExclamationCircle size={16} />,
      showLabel: true,
    },
  ];

  // Opciones para el selector de tipo de cliente
  const clientTypeOptions = [
    {
      value: 'all',
      label: translate('dashboard.clients.filters.allClientTypes'),
      icon: <FaIcons.FaUsers size={20} />,
      showLabel: true,
    },
    {
      value: '1',
      label: translate('global.package', 'capital') || 'Package',
      icon: <BiPackage size={20} />,
      showLabel: true,
    },
    {
      value: '7',
      label: translate('global.BLI') || 'BLI',
      icon: <FaRegBuilding size={20} />,
      showLabel: true,
    },
  ];

  // Opciones para el selector de rol
  const roleOptions = [
    {
      value: 'all',
      label: translate('dashboard.companies.detail.clients.filters.role') || 'Todos los roles',
      icon: <FaIcons.FaUserFriends size={18} />,
      showLabel: true,
    },
    {
      value: '1',
      label: translate('dashboard.companies.detail.clients.roles.customer'),
      icon: getClientConfRoleInfo(1, 18).icon,
      showLabel: true,
    },
    {
      value: '2',
      label: translate('dashboard.companies.detail.clients.roles.deliveryAgent'),
      icon: getClientConfRoleInfo(2, 18).icon,
      showLabel: true,
    },
    {
      value: '3',
      label: translate('dashboard.companies.detail.clients.roles.admin'),
      icon: getClientConfRoleInfo(3, 18).icon,
      showLabel: true,
    },
  ];

  // Cargar clientes asignados cuando se abre el modal
  useEffect(() => {
    if (!isOpen || !company) return;

    const loadAssignedClients = async () => {
      try {
        const companyId = company?.companyId || company?.id;
        if (companyId) {
          const result = await getAssignedMembersById(companyId, 1, 1000);
          let clientsList = [];
          
          if (result?.members && Array.isArray(result.members)) {
            clientsList = result.members;
          } else if (Array.isArray(result)) {
            clientsList = result;
          } else if (result?.payload) {
            if (Array.isArray(result.payload)) {
              clientsList = result.payload;
            } else if (result.payload.items) {
              clientsList = result.payload.items || [];
            }
          } else if (result?.data) {
            clientsList = Array.isArray(result.data) ? result.data : [];
          } else if (result?.items) {
            clientsList = result.items || [];
          }
          
          const ids = clientsList.map(c => 
            c?.IdCliente || c?.userId || c?.id || c?.IdUsuario || c?.clientId
          ).filter(Boolean);
          setAssignedClientIds(ids);
        }
      } catch (err) {
        console.error('Error loading assigned clients:', err);
      }
    };

    const initializeData = async () => {
      setAddedCountInSession(0);
      await loadAssignedClients();
      setCurrentPage(1);
      setFilters({});
      setSelectedField('name');
      setSelectedStatus('all');
      setSelectedClientType('all');
      setSelectedRole('all');
      isInitialMount.current = true;
      // Cargar clientes después de obtener los IDs asignados
      setTimeout(() => {
        loadClients();
        isInitialMount.current = false;
      }, 100);
    };

    initializeData().catch(ignoreAbort);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen, company, getAssignedMembersById]);

  // Cargar clientes cuando cambien los filtros, página o itemsPerPage
  useEffect(() => {
    if (isInitialMount.current || !isOpen || !company) return;

    loadClients();
  }, [filters, currentPage, itemsPerPage, assignedClientIds]);

  const loadClients = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const liveController = new AbortController();
    abortControllerRef.current = liveController;
    setLoading(true);

    try {
      // Preparar filtros para el servidor (igual que en useClients.js)
      const serverFilters = {
        currentPage,
        itemsPerPage,
      };

      // Validar y agregar clientId si es un número válido
      if (filters.clientId) {
        const parsedClientId = parseInt(filters.clientId, 10);
        if (!isNaN(parsedClientId) && parsedClientId > 0) {
          serverFilters.clientId = parsedClientId;
        }
      }

      // Agregar otros filtros de texto
      if (filters.name) serverFilters.name = filters.name;
      if (filters.lastName) serverFilters.lastName = filters.lastName;
      if (filters.email) serverFilters.email = filters.email;
      if (filters.userName) serverFilters.userName = filters.userName;
      if (filters.phoneNumber) serverFilters.phoneNumber = filters.phoneNumber;
      if (filters.externalId) serverFilters.externalId = filters.externalId;
      
      // Agregar filtro de status si existe
      if (filters.status) {
        const parsedStatus = parseInt(filters.status, 10);
        if (!isNaN(parsedStatus) && parsedStatus > 0) {
          serverFilters.statusId = parsedStatus;
        }
      }

      // Agregar filtro de clientType si existe
      if (filters.clientType) {
        const parsedClientType = parseInt(filters.clientType, 10);
        if (!isNaN(parsedClientType) && parsedClientType > 0) {
          serverFilters.clientType = parsedClientType;
        }
      }

      // Agregar filtro de rol (IdConfRoles) si existe
      if (filters.role) {
        const parsedRole = parseInt(filters.role, 10);
        if (!isNaN(parsedRole) && parsedRole > 0) {
          serverFilters.confRoleId = parsedRole;
        }
      }

      const response = await getClientsByFilter(
        abortControllerRef.current.signal,
        serverFilters
      );

      let clientsList = response?.itemsFind || [];
      
      // Filtrar clientes ya asignados
      clientsList = clientsList.filter(client => {
        const clientId = client?.IdCliente || client?.userId || client?.id || client?.IdUsuario || client?.clientId;
        return !assignedClientIds.includes(clientId);
      });

      // Usar los datos de paginación del servidor
      const serverTotalItems = response?.totalItems || 0;
      const serverTotalPages = response?.totalPages || 1;
      const serverItemsPerPage = response?.itemsPerPage || itemsPerPage;

      // Ajustar totales después de filtrar clientes asignados
      const filteredCount = clientsList.length;
      const originalCount = response?.itemsFind?.length || 0;
      const filteredOut = originalCount - filteredCount;

      setClients(clientsList);
      setTotalItems(Math.max(0, serverTotalItems - filteredOut));
      setTotalPages(Math.max(1, Math.ceil((serverTotalItems - filteredOut) / serverItemsPerPage)));
      
      // Si la página actual está vacía después de filtrar, volver a la página 1
      if (filteredCount === 0 && currentPage > 1) {
        setCurrentPage(1);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading clients:', err);
        if (showError) {
          showError(translate('dashboard.companies.detail.clients.addModal.errors.loadError'));
        }
      }
    } finally {
      // Only the request that was not superseded may clear the flag: an
      // aborted one used to switch it off for its replacement, blanking the
      // view until the new data arrived.
      if (!liveController.signal.aborted) setLoading(false);
    }
  };

  // Handlers para SearchFilter (igual que en Clients/index.jsx)
  const handleSearch = (term, selectedField = 'name') => {
    setCurrentPage(1);
    
    if (term && term.trim()) {
      const trimmedTerm = term.trim();
      setFilters(prev => {
        const newFilters = { ...prev };
        // Limpiar todos los demás filtros de texto y usar solo el campo seleccionado
        delete newFilters.name;
        delete newFilters.lastName;
        delete newFilters.email;
        delete newFilters.clientId;
        delete newFilters.userName;
        delete newFilters.phoneNumber;
        delete newFilters.externalId;
        
        newFilters[selectedField] = trimmedTerm;
        return newFilters;
      });
    } else {
      // Si está vacío, limpiar solo los filtros de texto
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.name;
        delete newFilters.lastName;
        delete newFilters.email;
        delete newFilters.clientId;
        delete newFilters.userName;
        delete newFilters.phoneNumber;
        delete newFilters.externalId;
        return newFilters;
      });
    }
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    if (status && status !== 'all') {
      setFilters(prev => ({ ...prev, status }));
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.status;
        return newFilters;
      });
    }
  };

  const handleClientTypeChange = (clientType) => {
    setSelectedClientType(clientType);
    setCurrentPage(1);
    if (clientType && clientType !== 'all') {
      setFilters(prev => ({ ...prev, clientType }));
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.clientType;
        return newFilters;
      });
    }
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setCurrentPage(1);
    if (role && role !== 'all') {
      setFilters(prev => ({ ...prev, role }));
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.role;
        return newFilters;
      });
    }
  };

  const handleAssignClient = async (client) => {
    if (!company) return;
    if (!canUpdate) return;

    const companyCode = company?.companyCode || company?.CodUser;
    if (!companyCode) {
      if (showError) {
        showError(translate('dashboard.companies.detail.errors.companyCodeNotFound'));
      }
      return;
    }

    const clientId = client?.UserIdGL ;
    if (!clientId) {
      if (showError) {
        showError(translate('dashboard.companies.detail.clients.errors.clientIdNotFound'));
      }
      return;
    }

    setAssigningClientId(clientId);

    try {
      const abortController = new AbortController();
      
      // Campos opcionales que puedes enviar si están disponibles
      const additionalFields = {};
      if (client?.Nombre || client?.firstName) additionalFields.firstName = client.Nombre || client.firstName;
      if (client?.Apellidos || client?.lastName) additionalFields.lastName = client.Apellidos || client.lastName;
      if (client?.phone || client?.phoneNumber) additionalFields.phone = client.phone || client.phoneNumber;
      // Priorizar IdConfRoles sobre otros campos de rol
      const roleId = client?.IdConfRoles ?? client?.idconfroles ?? client?.IdRole ?? client?.registrationRole ?? client?.IdTipoCliente;
      if (roleId !== undefined && roleId !== null) {
        additionalFields.IdConfRoles = roleId;
        // También mantener registrationRole para compatibilidad con el backend
        additionalFields.registrationRole = roleId;
      }
      if (client?.accesibility !== undefined) additionalFields.accesibility = client.accesibility;
      if (client?.IdEstatus !== undefined || client?.status !== undefined) {
        const statusValue = client.IdEstatus !== undefined ? client.IdEstatus : client.status;
        additionalFields.status = statusValue === 1 ? 'active' : 'inactive';
      }
      
      const result = await assignClientToCompany(
        clientId, 
        companyCode, 
        additionalFields,
        abortController.signal
      );
      
      // Manejar diferentes respuestas del API
      if (result && (result.code === 200 || result.message === 'MEMBER_ASSIGNED')) {
        const clientName = formatClientName(client);
        
        // Agregar a la lista de asignados
        setAssignedClientIds(prev => [...prev, clientId]);
        setAddedCountInSession(prev => prev + 1);
        
        // Remover de la lista de disponibles
        setClients(prev => prev.filter(c => {
          const cId = c?.UserIdGL || c?.IdCliente || c?.userId || c?.id || c?.IdUsuario || c?.clientId;
          return cId !== clientId;
        }));
        
        // Actualizar totales después de remover el cliente
        setTotalItems(prev => Math.max(0, prev - 1));
        setTotalPages(prev => Math.max(1, Math.ceil((totalItems - 1) / itemsPerPage)));
        
        // Mostrar mensaje de éxito (el modal permanece abierto para seguir agregando)
        if (showSuccess) {
          showSuccess(
            translate('dashboard.companies.detail.clients.addModal.success.assignSuccess')
              .replace('{clientName}', clientName)
          );
        }
        
        // Notificar al componente padre para actualizar la lista de clientes
        if (onClientAdded) {
          onClientAdded();
        }
        
        // Solo limpiar estado de carga; no cerrar el modal para permitir agregar más clientes
        setAssigningClientId(null);
      } else {
        // Manejar errores específicos del API
        let errorMessage = translate('dashboard.companies.detail.clients.addModal.errors.assignError');
        
        if (result?.message === 'USER_ID_DOESNT_EXISTS') {
          errorMessage = translate('dashboard.companies.detail.clients.errors.userIdDoesntExists');
        } else if (result?.message === 'COMPANY_DOESNT_EXISTS') {
          errorMessage = translate('dashboard.companies.detail.clients.errors.companyDoesntExists');
        } else if (result?.message === 'ASSIGNMENT_ALREADY_EXISTS') {
          errorMessage = translate('dashboard.companies.detail.clients.addModal.errors.alreadyAssigned');
        } else if (result?.message) {
          errorMessage = result.message;
        }
        
        if (showError) {
          showError(errorMessage);
        }
        setAssigningClientId(null);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error assigning client:', err);
        if (showError) {
          let errorMessage = err.message || translate('dashboard.companies.detail.clients.addModal.errors.assignError');
          if (err.message === 'ASSIGNMENT_ALREADY_EXISTS') {
            errorMessage = translate('dashboard.companies.detail.clients.addModal.errors.alreadyAssigned');
          }
          showError(errorMessage);
        }
      }
      setAssigningClientId(null);
    }
  };

  // Los clientes ya vienen paginados del servidor, no necesitamos hacer slice
  const paginatedClients = clients;

  const handleClose = () => {
    setFilters({});
    setSelectedField('name');
    setSelectedStatus('all');
    setSelectedClientType('all');
    setSelectedRole('all');
    setClients([]);
    setCurrentPage(1);
    setAssigningClientId(null);
    setAssignedClientIds([]);
    setAddedCountInSession(0);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={translate('dashboard.companies.detail.clients.addModal.title')}
      size="lg"
    >
      <div className={styles.modalContent}>
        {!canUpdate ? (
          <div style={{ padding: "1rem" }}>
            <h6 style={{ marginBottom: 6 }}>
              {translate("dashboard.configuration.noPermissionTitle")}
            </h6>
            <div style={{ color: "var(--text-secondary, #6B7280)" }}>
              {translate("dashboard.clients.formSearch.NOT_ACCESS")}
            </div>
          </div>
        ) : (
          <>
            {/* Search Section - Using SearchFilter component */}
            <div className={styles.modalHeader}>
              <div className={styles.searchFilterWrapper}>
                <SearchFilter
                  placeholder={`${translate('global.search')} ...`}
                  onSearch={handleSearch}
                  onFilter={handleFilter}
                  activeFilters={filters}
                  loading={loading}
                  unifiedMode={true}
                  selectedField={selectedField}
                  onFieldChange={setSelectedField}
                  fieldOptions={fieldOptions}
                  statusFilter={false}
                  roleFilter={false}
                  clientTypeFilter={false}
                />
              </div>

              {/* Results Info Section */}
              {!loading && clients.length > 0 && (
                <div className={styles.resultsInfo}>
                  <span className={styles.resultsCount}>
                    {totalItems} {totalItems === 1 
                      ? translate('dashboard.companies.detail.clients.addModal.resultsCount.single')
                      : translate('dashboard.companies.detail.clients.addModal.resultsCount.plural')
                    }
                  </span>
                  <div className={styles.itemsPerPageSelector}>
                    <label htmlFor="itemsPerPage" className={styles.itemsPerPageLabel}>
                      {translate('dashboard.companies.detail.clients.addModal.itemsPerPage')}:
                    </label>
                    <select
                      id="itemsPerPage"
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className={styles.itemsPerPageSelect}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Session summary: clients added without closing modal */}
            {addedCountInSession > 0 && (
              <div className={styles.sessionSummary} role="status">
                <FaIcons.FaCheckCircle className={styles.sessionSummaryIcon} />
                <span>
                  {addedCountInSession === 1
                    ? translate('dashboard.companies.detail.clients.addModal.sessionSummary.single')
                    : translate('dashboard.companies.detail.clients.addModal.sessionSummary.plural').replace('{count}', addedCountInSession)}
                </span>
              </div>
            )}

            {/* Clients List - Improved Structure */}
            <div className={styles.clientsList}>
              {loading ? (
                <div className={styles.loadingContainer}>
                  <FaSpinner className={styles.spinner} />
                  <span>{translate('dashboard.companies.detail.clients.addModal.loading')}</span>
                </div>
              ) : paginatedClients.length === 0 ? (
                <div className={styles.emptyContainer}>
                  <div className={styles.emptyIcon}>👤</div>
                  <p className={styles.emptyTitle}>
                    {translate('dashboard.companies.detail.clients.addModal.empty.title')}
                  </p>
                  <p className={styles.emptyDescription}>
                    {Object.keys(filters).length > 0
                      ? translate('dashboard.companies.detail.clients.addModal.empty.tryOtherTerms')
                      : translate('dashboard.companies.detail.clients.addModal.empty.noClientsAvailable')
                    }
                  </p>
                </div>
              ) : (
                <div className={styles.clientsGrid}>
                  {paginatedClients.map((client) => {
                    const clientId = client?.UserIdGL || client?.IdCliente || client?.userId || client?.id || client?.IdUsuario || client?.clientId;
                    const clientIdForAssignment = client?.UserIdGL;
                    const isAssigning = assigningClientId === clientIdForAssignment;
                    // Priorizar IdConfRoles sobre otros campos
                    const roleId = client?.IdConfRoles;
                    const isActive = (client?.IdEstatus === 1 || client?.status === 1);
                    const { label: roleLabel, icon: roleIcon } = roleId ? getClientConfRoleInfo(roleId, 12) : { label: null, icon: null };

                    return (
                      <div key={clientId} className={styles.clientCard}>
                        {/* Client Header */}
                        <div className={styles.clientCardHeader}>
                          <div className={styles.clientAvatar}>
                            {formatClientName(client)?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className={styles.clientHeaderInfo}>
                            <div className={styles.clientNameRow}>
                              <h4 className={styles.clientName}>
                                {formatClientName(client) || translate('dashboard.companies.detail.clients.addModal.client.noName')}
                              </h4>
                            </div>
                            <div className={styles.clientBadgesRow}>
                              {roleId && roleLabel && (
                                <Badge variant="neutral" icon={roleIcon} showIcon={true} size="sm">
                                  {roleLabel}
                                </Badge>
                              )}
                              <Badge variant={getStatusVariant(isActive)} size="sm">
                                {isActive 
                                  ? translate('dashboard.companies.status.active') 
                                  : translate('dashboard.companies.status.inactive')
                                }
                              </Badge>
                            </div>
                          </div>
                        </div>

                        {/* Client Details */}
                        <div className={styles.clientCardBody}>
                          {(client?.Email || client?.email) && (
                            <div className={styles.clientDetailItem}>
                              <span className={styles.clientDetailLabel}>
                                {translate('dashboard.companies.detail.clients.addModal.client.emailLabel')}
                              </span>
                              <span className={styles.clientDetailValue}>{client.Email || client.email}</span>
                            </div>
                          )}
                          {(client?.phone || client?.phoneNumber) && (
                            <div className={styles.clientDetailItem}>
                              <span className={styles.clientDetailLabel}>
                                {translate('dashboard.companies.detail.clients.addModal.client.phoneLabel')}
                              </span>
                              <span className={styles.clientDetailValue}>{client.phone || client.phoneNumber}</span>
                            </div>
                          )}
                        </div>

                        {/* Client Action */}
                        <div className={styles.clientCardFooter}>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleAssignClient(client)}
                            disabled={isAssigning}
                            loading={isAssigning}
                            fullWidth
                          >
                            {isAssigning 
                              ? translate('dashboard.companies.detail.clients.addModal.adding')
                              : translate('dashboard.companies.detail.clients.addModal.addButton')
                            }
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination - Improved */}
            {!loading && clients.length > 0 && totalPages > 1 && (
              <div className={styles.pagination}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  {translate('pagination.previousPage')}
                </Button>
                <div className={styles.paginationInfo}>
                  <span className={styles.pageInfo}>
                    {translate('dashboard.companies.detail.clients.addModal.pagination.page')
                      .replace('{current}', currentPage)
                      .replace('{total}', totalPages)
                    }
                  </span>
                  <span className={styles.itemsInfo}>
                    {translate('dashboard.companies.detail.clients.addModal.pagination.items')
                      .replace('{start}', ((currentPage - 1) * itemsPerPage) + 1)
                      .replace('{end}', Math.min(currentPage * itemsPerPage, totalItems))
                      .replace('{total}', totalItems)
                    }
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  {translate('pagination.nextPage')}
                </Button>
              </div>
            )}

            {/* Footer */}
            <div className={styles.modalFooter}>
              <Button variant="outline" onClick={handleClose}>
                {translate('global.cancel')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AddClientModal;

